import { isJwApiError } from "../api/error.js";
import { cookieManager } from "./cookies.js";
import { JwAuthError } from "./error.js";
import { EventManager } from "../../../_node_modules/@jwplayer/fe-common/module/event-manager/index.js";

//#region ../base/auth/manager.ts
/**
* Handles authentication and automatic session refreshing.
*/
var AuthManager = class {
	events = new EventManager();
	/**
	* Cached 'session' object
	*/
	cache;
	lastRefresh;
	opts;
	/**
	* Cached token
	*/
	cachedToken;
	constructor(opts) {
		this.opts = { ...opts };
		if (typeof document === "object" && "addEventListener" in document) document.addEventListener("visibilitychange", () => {
			if (!document.hidden) this.hasTokenExpired();
		});
	}
	/**
	* Clear the cached session and auth token (cookie).
	*/
	clear() {
		this.cachedToken = void 0;
		cookieManager.deleteCookie(this.opts.authCookieName());
		this.cache = void 0;
		this.events.emit("cleared");
	}
	/**
	* API call needs to be implemented in child class
	*/
	async authenticateApi(_opts) {
		throw new Error("Not implemented");
	}
	/**
	* API call needs to be implemented in child class
	*/
	async loginApi(_params, _opts) {
		throw new Error("Not implemented");
	}
	/**
	* Make an API call to refresh a session using an existing auth token.
	*
	* @private
	*/
	async refresh(opts) {
		if (!(this.getToken() || opts?.authToken)) throw new JwAuthError("MISSING_TOKEN");
		try {
			const res = await this.authenticateApi(opts);
			this.setCache(res.token, res.obj);
			return res.obj;
		} catch (err) {
			if (isJwApiError(err) && String(err.response?.status).startsWith("4")) this.clear();
			throw err;
		}
	}
	/**
	* Returns an existing session if there is one. If not, then an API
	* call is made to refresh the session from an auth cookie.
	*/
	async authenticate(opts) {
		const cache = this.getCache();
		if (cache) return cache;
		return this.refresh(opts);
	}
	async login(params, opts) {
		const res = await this.loginApi(params, opts);
		this.setCache(res.token, res.obj);
		return res.obj;
	}
	logout() {
		this.clear();
	}
	hasTokenExpired = (() => {
		let tokenRefreshing = false;
		return () => {
			if (typeof this.lastRefresh === "number") {
				const tokenAge = Date.now() - this.lastRefresh;
				const { expired: maxTokenAge, stale: staleTokenAge } = this.opts.authTokenTtl();
				if (tokenAge > maxTokenAge) {
					this.clear();
					this.events.emit("expired");
					return true;
				}
				if (tokenAge > staleTokenAge && !tokenRefreshing) {
					tokenRefreshing = true;
					this.authenticateApi().then((res) => {
						this.setCache(res.token, res.obj);
					}).catch(() => {}).finally(() => {
						tokenRefreshing = false;
					});
				}
			}
			return false;
		};
	})();
	getToken() {
		if (this.hasTokenExpired()) return;
		if (!this.cachedToken) this.cachedToken = cookieManager.getCookie(this.opts.authCookieName()) || void 0;
		return this.cachedToken;
	}
	getCache() {
		if (this.hasTokenExpired()) return;
		if (!this.cache) return;
		return this.cache;
	}
	/**
	* Set auth token (and cached session object)
	*/
	setCache(token, cache) {
		this.lastRefresh = Date.now();
		this.cachedToken = token;
		this.cache = cache ? structuredClone(cache) : void 0;
		cookieManager.setCookie(this.opts.authCookieName(), token);
		this.events.emit("authenticated", token, cache);
	}
	modifyCache(fn) {
		const currentCache = this.getCache();
		if (!currentCache) return;
		const newCache = fn(structuredClone(currentCache));
		this.cache = structuredClone(newCache);
		this.events.emit("cacheModified", this.cache);
	}
	subscribe = this.events.subscribe.bind(this);
};

//#endregion
export { AuthManager };